import { useState, useRef, useEffect, useCallback } from "react";
import { sentryErrorLog } from "../../utils/sentry";
import { useAudioAnalysis } from "../AudioVisualizer";
import { useWriteEventLog } from "../../hooks/useWriteEventLog";
import { exitAssessment } from "../../lib/assessmentExit";

const AUDIO_THRESHOLD = 165;

type MicCheckStatus =
  | "initial"
  | "recording"
  | "success"
  | "failed"
  | "no-audio"
  | "final";

interface UseMicCheckProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onRetry: () => void;
  submissionId: string | null;
  taskType?: "activity" | "benchmark";
}

export function useMicCheck({
  open,
  onClose,
  onSuccess,
  onRetry,
  submissionId,
  taskType = "activity",
}: UseMicCheckProps) {
  const [micStatus, setMicStatus] = useState<MicCheckStatus>("initial");
  const [audioDetected, setAudioDetected] = useState(false);
  const [liveRecordingFrequencyLevel, setLiveRecordingFrequencyLevel] =
    useState(0);
  const [isPlayingInstructions, setIsPlayingInstructions] = useState(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const instructionsAudioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const noAudioTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioDetectedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxFrequencyLevelRef = useRef<number>(0);
  const audioDetectedLoggedRef = useRef<boolean>(false);
  const miccheckStartTimeRef = useRef<string | null>(null);
  const { writeEventLog } = useWriteEventLog();

  const { startAnalysis, stopAnalysis } = useAudioAnalysis({
    onFrequencyData: (level) => {
      setLiveRecordingFrequencyLevel(level);
      if (level > maxFrequencyLevelRef.current) {
        maxFrequencyLevelRef.current = level;
      }
    },
    onAudioDetected: (detected) => {
      if (detected && !audioDetected) {
        setAudioDetected(true);
        if (noAudioTimeoutRef.current) {
          clearTimeout(noAudioTimeoutRef.current);
          noAudioTimeoutRef.current = null;
        }
        if (!audioDetectedLoggedRef.current) {
          audioDetectedLoggedRef.current = true;
          writeEventLog("mic_check.audio_detected.v1", {
            message: "audio-detected-during-mic-check",
            component: "MicCheckModal",
            function: "onAudioDetected",
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            miccheck_start_time: miccheckStartTimeRef.current,
            submission_id: submissionId || "unknown",
            audio_threshold: AUDIO_THRESHOLD,
            max_frequency_level_detected: maxFrequencyLevelRef.current,
          }).catch((logError: unknown) => {
            console.error("Failed to log audio detected event:", logError);
            sentryErrorLog(logError, [
              { label: "component", value: "MicCheckModal" },
              { label: "function", value: "writeEventLog" },
              { label: "event_type", value: "audio_detected" },
            ]);
          });
        }

        audioDetectedTimeoutRef.current = setTimeout(() => {
          writeEventLog("mic_check.audio_detected.timeout.v1", {
            message: "audio-detected-timeout-triggered",
            component: "MicCheckModal",
            function: "onAudioDetected",
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            miccheck_start_time: miccheckStartTimeRef.current,
            submission_id: submissionId || "unknown",
            timeout_duration_seconds: 30,
          }).catch((logError: unknown) => {
            console.error(
              "Failed to log audio detected timeout event:",
              logError,
            );
            sentryErrorLog(logError, [
              { label: "component", value: "MicCheckModal" },
              { label: "function", value: "writeEventLog" },
              { label: "event_type", value: "audio_detected_timeout" },
            ]);
          });

          handleStopRecording();
        }, 30000);
      }
    },
    audioThreshold: AUDIO_THRESHOLD,
  });

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (noAudioTimeoutRef.current) {
      clearTimeout(noAudioTimeoutRef.current);
      noAudioTimeoutRef.current = null;
    }
    if (audioDetectedTimeoutRef.current) {
      clearTimeout(audioDetectedTimeoutRef.current);
      audioDetectedTimeoutRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    stopAnalysis();
    setLiveRecordingFrequencyLevel(0);
    setIsPlayingInstructions(false);
    instructionsAudioRef.current?.pause();
    instructionsAudioRef.current = null;
    maxFrequencyLevelRef.current = 0;
  }, [stopAnalysis]);

  const handleMicrophoneTap = async () => {
    if (micStatus !== "initial") return;

    writeEventLog("mic_check.started.v1", {
      message: "started",
      component: "MicCheckModal",
      function: "handleMicrophoneTap",
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      miccheck_start_time: miccheckStartTimeRef.current,
      submission_id: submissionId || "unknown",
      outcome: "started",
      task_type: taskType,
    }).catch((logError: unknown) => {
      console.error("Failed to log mic check started event:", logError);
      sentryErrorLog(logError, [
        { label: "component", value: "MicCheckModal" },
        { label: "function", value: "writeEventLog" },
        { label: "event_type", value: "mic_check_started" },
      ]);
    });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      setMicStatus("recording");
      startAnalysis(stream);

      noAudioTimeoutRef.current = setTimeout(() => {
        if (!audioDetected) {
          cleanup();
          setMicStatus("no-audio");

          writeEventLog("mic_check.no_audio_detected.v1", {
            message: "failure-no-audio-detected-timeout",
            error_message: "No audio detected after 5 seconds",
            error_type: "NoAudioDetected",
            component: "MicCheckModal",
            function: "handleMicrophoneTap",
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            "miccheck-start-time": miccheckStartTimeRef.current,
            submission_id: submissionId || "unknown",
            outcome: "failure",
          }).catch((logError: unknown) => {
            console.error("Failed to log no audio event:", logError);
            sentryErrorLog(logError, [
              { label: "component", value: "MicCheckModal" },
              { label: "function", value: "writeEventLog" },
              { label: "event_type", value: "no_audio_detected" },
            ]);
          });
        }
      }, 5000);
    } catch (error) {
      console.error("Microphone access error:", error);
      sentryErrorLog(error, [
        { label: "component", value: "MicCheckModal" },
        { label: "function", value: "handleMicrophoneTap" },
      ]);
      setMicStatus("failed");

      writeEventLog("mic_check.microphone_access_denied.v1", {
        message: "failure-no-microphone-access-granted",
        error_message: error instanceof Error ? error.message : "Unknown error",
        error_type: error instanceof Error ? error.name : "Unknown",
        component: "MicCheckModal",
        function: "handleMicrophoneTap",
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        "miccheck-start-time": miccheckStartTimeRef.current,
        submission_id: submissionId || "unknown",
        outcome: "failure",
      }).catch((logError: unknown) => {
        console.error("Failed to log mic check failure event:", logError);
        sentryErrorLog(logError, [
          { label: "component", value: "MicCheckModal" },
          { label: "function", value: "writeEventLog" },
          { label: "event_type", value: "mic_access_failed" },
        ]);
      });
    }
  };

  const handleListenToInstructions = async () => {
    try {
      if (isPlayingInstructions) {
        return;
      }
      const audio = new Audio("/audio/MicCheckInstructions.mp3");
      instructionsAudioRef.current = audio;

      setIsPlayingInstructions(true);

      audio.addEventListener("ended", () => {
        setIsPlayingInstructions(false);
        instructionsAudioRef.current = null;
      });

      audio.addEventListener("error", (error) => {
        console.error("Error playing instructions audio:", error);
        setIsPlayingInstructions(false);
        instructionsAudioRef.current = null;
        sentryErrorLog(error, [
          { label: "component", value: "MicCheckModal" },
          { label: "function", value: "handleListenToInstructions" },
        ]);
      });

      await audio.play();
    } catch (error) {
      console.error("Failed to play instructions audio:", error);
      setIsPlayingInstructions(false);
      sentryErrorLog(error, [
        { label: "component", value: "MicCheckModal" },
        { label: "function", value: "handleListenToInstructions" },
      ]);
    }
  };

  const handleListenToWaitInstructions = async () => {
    try {
      if (isPlayingInstructions) {
        return;
      }
      const audio = new Audio("/audio/MicCheckWaitForTeacher.mp3");
      instructionsAudioRef.current = audio;

      setIsPlayingInstructions(true);

      audio.addEventListener("ended", () => {
        setIsPlayingInstructions(false);
        instructionsAudioRef.current = null;
      });

      audio.addEventListener("error", (error) => {
        console.error("Error playing wait instructions audio:", error);
        setIsPlayingInstructions(false);
        instructionsAudioRef.current = null;
        sentryErrorLog(error, [
          { label: "component", value: "MicCheckModal" },
          { label: "function", value: "handleListenToWaitInstructions" },
        ]);
      });

      await audio.play();
    } catch (error) {
      console.error("Failed to play wait instructions audio:", error);
      setIsPlayingInstructions(false);
      sentryErrorLog(error, [
        { label: "component", value: "MicCheckModal" },
        { label: "function", value: "handleListenToWaitInstructions" },
      ]);
    }
  };

  const handleStopRecording = () => {
    if (audioDetectedTimeoutRef.current) {
      clearTimeout(audioDetectedTimeoutRef.current);
      audioDetectedTimeoutRef.current = null;
    }
    cleanup();
    setMicStatus("success");
  };

  const handleStartBenchmark = () => {
    cleanup();
    setMicStatus("final");
  };

  const handleMicCheckComplete = () => {
    cleanup();

    writeEventLog("mic_check.completed.v1", {
      message: "success",
      component: "MicCheckModal",
      function: "handleMicCheckComplete",
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      "miccheck-start-time": miccheckStartTimeRef.current,
      audio_detected: audioDetected,
      audio_threshold: AUDIO_THRESHOLD,
      max_frequency_level_detected: maxFrequencyLevelRef.current,
      submission_id: submissionId || "unknown",
      outcome: "success",
    }).catch((logError: unknown) => {
      console.error("Failed to log mic check success event:", logError);
      sentryErrorLog(logError, [
        { label: "component", value: "MicCheckModal" },
        { label: "function", value: "writeEventLog" },
        { label: "event_type", value: "mic_check_success" },
      ]);
    });

    onSuccess();
  };

  const handleRetry = () => {
    cleanup();
    setMicStatus("initial");
    setAudioDetected(false);
    audioDetectedLoggedRef.current = false;
    onRetry();
  };

  const handleCancel = () => {
    cleanup();
    setAudioDetected(false);
    onClose();
    exitAssessment();
  };

  const handleSkipMicCheck = () => {
    cleanup();
    setAudioDetected(false);

    writeEventLog("mic_check.skipped.v1", {
      message: "skipped",
      component: "MicCheckModal",
      function: "handleSkipMicCheck",
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      "miccheck-start-time": miccheckStartTimeRef.current,
      audio_detected: audioDetected.toString(),
      submission_id: submissionId || "unknown",
      outcome: "skipped",
      skip_reason: "user_clicked_confetti_emoji",
    }).catch((logError: unknown) => {
      console.error("Failed to log mic check skip event:", logError);
      sentryErrorLog(logError, [
        { label: "component", value: "MicCheckModal" },
        { label: "function", value: "writeEventLog" },
        { label: "event_type", value: "mic_check_skipped" },
      ]);
    });

    onSuccess();
  };

  const handleAudioDetected = (detected: boolean) => {
    setAudioDetected(detected);
  };

  useEffect(() => {
    if (open && miccheckStartTimeRef.current === null) {
      audioDetectedLoggedRef.current = false;
      miccheckStartTimeRef.current = new Date().toISOString();
    }
  }, [open]);

  return {
    micStatus,
    audioDetected,
    liveRecordingFrequencyLevel,
    isPlayingInstructions,
    handleMicrophoneTap,
    handleListenToInstructions,
    handleListenToWaitInstructions,
    handleStopRecording,
    handleRetry,
    handleCancel,
    handleSkipMicCheck,
    handleAudioDetected,
    handleStartBenchmark,
    handleMicCheckComplete,
    taskType,
  };
}
